/**
 * Tabela (item TABLE): o formato do conteúdo e as opções de exibição.
 *
 * `value` do item é `{ columns, rows }`, tudo texto puro. Estilo, zebra e cor ficam nas
 * configs do item (`tableStyle`, `tableZebra`, `calloutColor`). O PDF (PdfEngine e
 * `pdf-renderer/`) desenha com estes mesmos valores; o editor, a lixeira, os favoritos
 * e a prévia antiga também. A tabela é criada e editada só pela web.
 */
export interface DocumentTableValue {
    columns: string[];
    rows: string[][];
    /**
     * Largura de cada coluna em % (uma por coluna, somando 100). Ausente => as colunas se
     * ajustam ao conteúdo. A API normaliza igual a `normalizeTableWidths`.
     */
    widths?: number[];
}

export type TableStyleKey = 'filled' | 'lines' | 'grid';

export interface TableStyleOption {
    key: TableStyleKey;
    labelKey: string;
}

export const TABLE_STYLES: readonly TableStyleOption[] = [
    { key: 'filled', labelKey: 'document.edit.tableCard.styleFilled' },
    { key: 'lines', labelKey: 'document.edit.tableCard.styleLines' },
    { key: 'grid', labelKey: 'document.edit.tableCard.styleGrid' },
];

/** Mesmos tetos da API (table-value.util). O mínimo de 2 colunas é regra do editor. */
export const TABLE_LIMITS = {
    MIN_COLUMNS: 2,
    MAX_COLUMNS: 6,
    MAX_ROWS: 100,
    MAX_HEADER_LENGTH: 80,
    MAX_CELL_LENGTH: 500,
    INITIAL_COLUMNS: 2,
    INITIAL_ROWS: 2,
    /** Menor coluna, em % da tabela (a API usa o mesmo). */
    MIN_COLUMN_WIDTH: 5,
} as const;

export const DEFAULT_TABLE_STYLE: TableStyleKey = 'filled';
export const DEFAULT_TABLE_ZEBRA = true;

/** Tabela vazia de um campo recém-criado: 2 colunas e 2 linhas. */
export function initialTableValue(): DocumentTableValue {
    return {
        columns: Array.from({ length: TABLE_LIMITS.INITIAL_COLUMNS }, () => ''),
        rows: Array.from({ length: TABLE_LIMITS.INITIAL_ROWS }, () =>
            Array.from({ length: TABLE_LIMITS.INITIAL_COLUMNS }, () => '')
        ),
    };
}

/** Lê o `value` gravado; cada linha sai com uma célula por coluna. */
export function parseTableValue(raw: unknown): DocumentTableValue {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw))
        return { columns: [], rows: [] };

    const record = raw as Record<string, unknown>;
    const columns = Array.isArray(record['columns'])
        ? (record['columns'] as unknown[]).map(c => (typeof c === 'string' ? c : ''))
        : [];
    const rows = (Array.isArray(record['rows']) ? (record['rows'] as unknown[]) : [])
        .filter((row): row is unknown[] => Array.isArray(row))
        .map(row => columns.map((_, i) => (typeof row[i] === 'string' ? (row[i] as string) : '')));

    const rawWidths = record['widths'];
    const widths =
        Array.isArray(rawWidths) &&
        rawWidths.length === columns.length &&
        rawWidths.length > 0 &&
        rawWidths.every(w => typeof w === 'number' && Number.isFinite(w) && w > 0)
            ? (rawWidths as number[])
            : undefined;

    return widths ? { columns, rows, widths } : { columns, rows };
}

/**
 * Larguras em % somando 100, com o mínimo por coluna e duas casas: a mesma conta do
 * `sanitizeWidths` da API, para o que o editor mostra ser o que fica gravado.
 */
export function normalizeTableWidths(raw: number[]): number[] {
    const min = TABLE_LIMITS.MIN_COLUMN_WIDTH;
    const total = raw.reduce((sum, v) => sum + v, 0);
    let widths = raw.map(v => (v / total) * 100);

    for (let pass = 0; pass < 3 && widths.some(w => w < min - 1e-9); pass++) {
        const small = widths.map(w => w < min);
        const rest = widths.reduce((sum, w, i) => (small[i] ? sum : sum + w), 0);
        const room = 100 - min * small.filter(Boolean).length;
        widths = widths.map((w, i) => (small[i] ? min : (w / rest) * room));
    }

    const rounded = widths.map(w => Math.round(w * 100) / 100);
    const head = rounded.slice(0, -1);
    const last = Math.round((100 - head.reduce((sum, w) => sum + w, 0)) * 100) / 100;
    return [...head, last];
}

/** Nova coluna: entra com a parte de uma coluna igual; as outras encolhem na proporção. */
export function widthsWithNewColumn(widths: number[] | undefined): number[] | undefined {
    if (!widths) return undefined;
    const count = widths.length + 1;
    return normalizeTableWidths([...widths.map(w => (w * (count - 1)) / count), 100 / count]);
}

/** Coluna removida: as que ficam dividem o espaço dela na proporção. */
export function widthsWithoutColumn(
    widths: number[] | undefined,
    column: number
): number[] | undefined {
    if (!widths) return undefined;
    const rest = widths.filter((_, i) => i !== column);
    return rest.length > 0 ? normalizeTableWidths(rest) : undefined;
}

/**
 * Arrastar a borda entre a coluna `column` e a seguinte: uma ganha o que a outra perde,
 * as demais não mudam. Respeita o mínimo das duas.
 */
export function resizeTableWidths(widths: number[], column: number, deltaPct: number): number[] {
    const min = TABLE_LIMITS.MIN_COLUMN_WIDTH;
    const pair = widths[column] + widths[column + 1];
    const left = Math.min(pair - min, Math.max(min, widths[column] + deltaPct));
    const next = [...widths];
    next[column] = left;
    next[column + 1] = pair - left;
    return next;
}

function sameWidths(a: number[] | undefined, b: number[] | undefined): boolean {
    if (!a || !b) return !a && !b;
    return a.length === b.length && a.every((w, i) => w === b[i]);
}

/**
 * Quebras de página da prévia (por linha IMPRESSA, as vazias não contam) convertidas para
 * as linhas do editor: índice da linha do editor => página que começa nela.
 */
export function editorRowPageBreaks(
    rows: string[][],
    breaks: { row: number; page: number }[] | undefined
): Record<number, number> {
    if (!breaks?.length) return {};

    const byPrinted = new Map(breaks.map(b => [b.row, b.page]));
    const result: Record<number, number> = {};
    let printed = 0;
    rows.forEach((row, index) => {
        if (!tableRowHasContent(row)) return;
        // Linha 0 quebrada = o cabeçalho ficou sozinho no pé da folha anterior.
        const page = byPrinted.get(printed);
        if (page !== undefined) result[index] = page;
        printed++;
    });
    return result;
}

/** Uma linha imprime se alguma célula tiver texto (mesma regra do PDF). */
export function tableRowHasContent(row: string[]): boolean {
    return row.some(cell => !!cell.trim());
}

export function resolveTableStyle(raw: string | null | undefined): TableStyleKey {
    const key = raw?.trim().toLowerCase();
    return TABLE_STYLES.find(s => s.key === key)?.key ?? DEFAULT_TABLE_STYLE;
}

export function resolveTableZebra(raw: boolean | null | undefined): boolean {
    return raw ?? DEFAULT_TABLE_ZEBRA;
}

/** Texto de célula como a API grava: uma linha só, aparado e cortado no teto. */
export function cleanTableText(text: string, max: number): string {
    return text
        .replace(/\s*[\r\n]+\s*/g, ' ')
        .trim()
        .slice(0, max)
        .trim();
}

/** Duas tabelas iguais célula a célula (evita salvar o que não mudou). */
export function sameTableValue(a: DocumentTableValue, b: DocumentTableValue): boolean {
    if (a.columns.length !== b.columns.length || a.rows.length !== b.rows.length) return false;
    if (!sameWidths(a.widths, b.widths)) return false;
    if (a.columns.some((c, i) => c !== b.columns[i])) return false;
    return a.rows.every(
        (row, r) => row.length === b.rows[r].length && row.every((c, i) => c === b.rows[r][i])
    );
}
